import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import PostDetailContent, {
  PostDetailHeaderActions,
} from "../PostDetail/PostDetailContent";
import PostDetailMessageCircleIcon from "../PostDetail/PostDetailMessageCircleIcon";
import PostDetailSendIcon from "../PostDetail/PostDetailSendIcon";
import CommentAvatar from "../Post/CommentAvatar";
import {
  PostDetailArticle,
  PostDetailBackLink,
  PostDetailBreadcrumb,
  PostDetailBreadcrumbCurrent,
  PostDetailBreadcrumbSep,
  PostDetailCommentsSlot,
  PostDetailMain,
  PostDetailNav,
  PostDetailPage,
} from "../PostDetail/PostDetail.styles";
import { shouldShowComments } from "../PostDetail/postDetailUtils";
import { bindPostDetailWistia } from "../PostDetail/wistiaPostPlayer";
import "../PostDetail/PostDetail.feed.scss";
import "../Post/Comments.scss";

const normalizeSuggestedPosts = (posts = []) =>
  posts
    .filter(Boolean)
    .map((item) => ({
      ...item,
      author: item.author || item.publisher || {},
      url: item.url || (item.slug ? `/blog/${item.slug}` : "#"),
      contentTypeId: item.contentTypeId || item.postContentTypeId || 1,
      publishedAt: item.publishedAt || item.createdAt || null,
    }));

/**
 * Preview is a sealed review modal:
 * - Looks like the live member page (header/footer visible for fidelity)
 * - Site chrome is view-only — FAQ / nav / footer must not leave the editor
 * - Exit only via “Back to editor” or Esc
 * - Video remains interactive (prod Wistia play)
 */
const PostEditorPreview = ({
  post,
  suggestedPosts = [],
  user,
  onClose,
}) => {
  const dialogRef = useRef(null);
  const previewUser = useMemo(
    () => user || { id: "preview", name: "Preview" },
    [user]
  );
  const suggested = useMemo(
    () => normalizeSuggestedPosts(suggestedPosts),
    [suggestedPosts]
  );

  const categoryLabel =
    post?.tags?.[0]?.name ||
    post?.educationTopics?.[0]?.name ||
    (post?.contentTypeId === 2 ? "Video" : "Blog");

  const showDiscussion = shouldShowComments(post, previewUser);
  const mediaId = post?.embeddedVideo?.mediaId;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("post-editor-preview-open");

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("post-editor-preview-open");
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  // Capture-phase guard: block any navigation out of preview (header FAQ,
  // footer, breadcrumbs, suggested posts, logo, search submit, etc.).
  // Video controls under .post-detail-video stay allowed.
  useEffect(() => {
    const root = dialogRef.current;
    if (!root) return undefined;

    const isAllowedTarget = (el) => {
      if (!(el instanceof Element)) return false;
      if (el.closest(".post-editor__preview-bar")) return true;
      if (el.closest(".post-detail-video")) return true;
      return false;
    };

    const blockLeavingPreview = (event) => {
      const el = event.target;
      if (!(el instanceof Element)) return;
      if (isAllowedTarget(el)) return;

      const anchor = el.closest("a[href]");
      if (anchor) {
        const href = (anchor.getAttribute("href") || "").trim();
        if (
          !href ||
          href === "#" ||
          href.startsWith("#") ||
          href.toLowerCase().startsWith("javascript:")
        ) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Header search submit / theme toggles / menus — freeze chrome actions
      if (el.closest(".post-editor__preview-chrome")) {
        const interactive = el.closest(
          "button, [role='button'], input, select, textarea, summary, label"
        );
        if (interactive) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    root.addEventListener("click", blockLeavingPreview, true);
    root.addEventListener("submit", blockLeavingPreview, true);

    return () => {
      root.removeEventListener("click", blockLeavingPreview, true);
      root.removeEventListener("submit", blockLeavingPreview, true);
    };
  }, []);

  useEffect(() => {
    if (!mediaId || post?.contentTypeId !== 2) {
      return undefined;
    }

    bindPostDetailWistia(mediaId);

    const delays = [200, 600, 1200, 2000];
    const timers = delays.map((ms) =>
      window.setTimeout(() => {
        bindPostDetailWistia(mediaId);
      }, ms)
    );

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [mediaId, post?.contentTypeId]);

  if (!post || typeof document === "undefined") return null;

  const noop = (event) => {
    if (event?.preventDefault) event.preventDefault();
  };

  const discussionSlot = showDiscussion ? (
    <PostDetailCommentsSlot $nested className="post-overview">
      <div className="add-comment post-comments">
        <div className="comments-shell">
          <header className="comments-shell__head">
            <div className="comments-shell__title-row">
              <h2 className="comments-shell__title">
                <PostDetailMessageCircleIcon className="comments-shell__icon" />
                Discussion
                <span className="comments-shell__count">(0)</span>
              </h2>
            </div>
            <p className="comments-shell__desc">
              Post your question here — {post.author?.name || "the analyst"} and
              the Bitcoin.live team respond directly on each post.
            </p>
          </header>
          <div className="comments-shell__body">
            <div className="comment-compose-card post-editor__preview-compose">
              <div className="comment-compose-card__body">
                <CommentAvatar
                  avatar={previewUser?.avatar?.small || null}
                  name={previewUser?.name || ""}
                />
                <div className="comment-compose-card__main">
                  <div className="comment-compose-card__editor post-detail-jodit">
                    <div
                      className="post-editor__preview-compose-fake"
                      aria-hidden="true"
                    >
                      Ask a question or share your read on the market…
                    </div>
                  </div>
                  <div className="comment-compose-card__footer">
                    <span className="comment-compose-card__hint">
                      Members only · be respectful, stay on topic
                    </span>
                    <button
                      type="button"
                      className="post-detail-comments__submit"
                      disabled
                    >
                      <PostDetailSendIcon />
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="comments-thread comments comment-list">
              <div className="comments-empty">
                <PostDetailMessageCircleIcon className="comments-empty__icon" />
                <p className="comments-empty__text">No comments yet.</p>
                <p className="comments-empty__subtext">
                  Be the first to start the discussion on this post.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PostDetailCommentsSlot>
  ) : null;

  return createPortal(
    <div
      ref={dialogRef}
      className="post-editor__preview-backdrop"
      role="presentation"
    >
      <PostDetailPage
        className="post-editor__preview-dialog post-detail-page"
        role="dialog"
        aria-modal="true"
        aria-labelledby="post-editor-preview-title"
      >
        <HomepageGlobalStyles />

        <div className="post-editor__preview-bar">
          <div className="post-editor__preview-bar-text">
            <p className="post-editor__preview-eyebrow">Preview mode</p>
            <h2 id="post-editor-preview-title">
              Member view — as published
            </h2>
          </div>
          <div className="post-editor__preview-bar-actions">
            <span className="post-editor__preview-hint">
              Nav is view-only · Esc to exit
            </span>
            <button
              type="button"
              className="post-editor__preview-close"
              onClick={onClose}
            >
              ← Back to editor
            </button>
          </div>
        </div>

        <div className="post-editor__preview-scroll">
          <div className="post-editor__preview-site">
            <div className="post-editor__preview-chrome" inert>
              <Header onSubscribeClick={noop} hideSpacer />
            </div>

            <PostDetailMain>
              <PostDetailArticle>
                <div className="post-editor__preview-chrome" inert>
                  <PostDetailNav>
                    <PostDetailBreadcrumb>
                      <PostDetailBackLink
                        to="/blogs/premium/"
                        onClick={noop}
                        tabIndex={-1}
                      >
                        <span className="material-icons" aria-hidden="true">
                          arrow_back
                        </span>
                        Feed
                      </PostDetailBackLink>
                      <PostDetailBreadcrumbSep>/</PostDetailBreadcrumbSep>
                      <PostDetailBreadcrumbCurrent>
                        {categoryLabel}
                      </PostDetailBreadcrumbCurrent>
                    </PostDetailBreadcrumb>

                    <PostDetailHeaderActions
                      post={post}
                      user={previewUser}
                      likePost={noop}
                      setAsFavourite={noop}
                      removeAsFavorite={noop}
                      showShare={false}
                      onShare={noop}
                    />
                  </PostDetailNav>
                </div>

                <PostDetailContent
                  post={post}
                  user={previewUser}
                  publicAccessVerified={true}
                  suggestedPosts={suggested}
                  commentsCount={0}
                  commentsSlot={discussionSlot}
                  isPreview
                />
              </PostDetailArticle>
            </PostDetailMain>

            <div className="post-editor__preview-chrome" inert>
              <Footer onSubscribeClick={noop} hideSubscribeCta />
            </div>
          </div>
        </div>
      </PostDetailPage>
    </div>,
    document.body
  );
};

export default PostEditorPreview;
